import React from 'react';
import { getIntl } from '../../../utils/intl';
import useFastenConnections from '../../../hooks/api/useFastenConnections';
import usePendingFastenConnections from '../../../hooks/api/usePendingFastenConnections';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import * as Card from '../../v2BaseComponents/Card';
import getDateString from '../../../utils/getDateString';
import { ActivityIndicator, Image, View } from 'react-native';
import Button from '../../v2BaseComponents/Button';
import Alert from '../../v2BaseComponents/Alert';
import { useProfilesTabNavigation } from '../../../utils/appNavigationNames';
import { getFastenLogoUrl, formatPlatformName } from '../../../utils/fastenUtils';
import MedicalProfileUsageIndicator from '../MedicalProfileUsageIndicator';
import { useMedicalProfileUsageMetric } from '../../../hooks/api/useMedicalProfileUsageMetric';
import { getUsageMetricTypeCacheKey } from '../../../hooks/api/cacheKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useHasPermission } from '../../v2BaseComponents/Permissions/hooks';

const intl = getIntl('fasten-connections-list', {
    fastenConnections: 'Patient Portals',
    pendingConnections: 'Pending Connections',
    connectedConnections: 'Connected',
    connected: 'Connected: {date}',
    noConnections: 'No Fasten Health connections found',
    logoAlt: 'Healthcare institution logo',
    pendingMessage: 'Your connection is being processed. This usually takes less than a minute.',
    pendingTimerMessage: 'Checking for updates... ({seconds}s remaining)',
    pendingProcessing: 'Processing your connection...',
    refreshing: 'Refreshing',
    lastRefreshed: 'Last refreshed: {date}',
    never: 'Never',
    automaticallyRefreshingThrough: 'Automatically refreshing through {date}',
    consentValidUntil: 'Consent valid until {date}',
    // `revoked`/`revokedMessage` mirror the backend connectionStatus enum; the copy reads
    // "Expired" by design.
    revoked: 'Expired',
    rejected: 'Rejected',
    reconnect: 'Reconnect',
    refreshLimitReached: 'Monthly Refresh Limit Reached',
    revokedMessage: 'The secure connection to this portal expired. This is normal and just needs to be reconnected.',
    rejectedMessage: 'Please double check your permissions with the EHR and try again.',
    importing: 'Importing',
    importingHealthRecords: 'Importing health records...',
    sandbox: 'Sandbox',
    tefca: 'TEFCA',
    tefcaNetwork: 'TEFCA network',
    tefcaReconnectMessage: 'Connect again via TEFCA to restore access.',
});

// Get next Monday at 18:00 UTC (matches backend cron: '0 18 * * 1')
// Displayed in the user's local timezone automatically by getDateString.
const getNextRefreshMonday = (from: Date): Date => {
    const dayOfWeek = from.getUTCDay(); // 0 = Sunday, 1 = Monday, ...
    const daysUntilMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 7 : 8 - dayOfWeek;
    const nextMonday = new Date(
        Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() + daysUntilMonday, 18, 0, 0, 0),
    );
    return nextMonday;
};

const CONSENT_EXPIRY_WARNING_DAYS = 7;

const isConsentExpiringSoon = (consentExpiresAt: string): boolean => {
    const expiryDate = new Date(consentExpiresAt);
    const now = new Date();
    const daysUntilExpiry = (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry > 0 && daysUntilExpiry <= CONSENT_EXPIRY_WARNING_DAYS;
};

export type FastenConnectionsListOwnProps = {
    medicalProfileId: string;
};

export type FastenConnectionsListProps = FastenConnectionsListOwnProps;

const FastenConnectionsList: React.FC<FastenConnectionsListProps> = ({ medicalProfileId }) => {
    const queryClient = useQueryClient();
    const { isLoading, connections } = useFastenConnections({
        medicalProfileId,
        refetchInterval: (query: any) => {
            const hasRefreshing = query.state.data?.some((c: any) => c.connectionStatus === 'refreshing');
            return hasRefreshing ? 15_000 : false;
        },
    });
    const { pendingConnections, isPolling, timeRemainingSeconds } = usePendingFastenConnections({ medicalProfileId });
    const { hasReachedLimit: hasReachedFastenRefreshLimit } = useMedicalProfileUsageMetric({
        medicalProfileId,
        metric: 'fasten-refresh',
    });
    // Reconnecting requires edit access to patient portals; hide the CTA when the viewer only has view access.
    const hasPortalEditPermission = useHasPermission(medicalProfileId, 'patientPortals', 'edit');

    // Invalidate usage cache when connections data changes (e.g. after reconnect webhook fires)
    React.useEffect(() => {
        queryClient.invalidateQueries({
            queryKey: getUsageMetricTypeCacheKey(medicalProfileId, 'fasten-refresh'),
        });
    }, [connections, queryClient, medicalProfileId]);

    const navigation = useProfilesTabNavigation();

    const handleNavigateToConnection = React.useCallback(
        (orgConnectionId: string) => {
            navigation.navigate('fasten-portal-connection', {
                profileId: medicalProfileId,
                orgConnectionId,
            });
        },
        [navigation, medicalProfileId],
    );

    // Hide backend connections that have a pending reconnect (same org_connection_id)
    const pendingOrgIds = new Set(pendingConnections.map((c) => c.org_connection_id));
    const filteredConnections = connections.filter((c) => !pendingOrgIds.has(c.orgConnectionId));

    const hasPending = pendingConnections.length > 0;
    const hasConnected = filteredConnections.length > 0;

    if (!isLoading && !hasPending && !hasConnected) {
        return null;
    }

    return (
        <AnimateLoadedContent isLoading={isLoading}>
            {() => (
                <Stack vertical fullWidth space={contentStackSpacing}>
                    <Stack horizontal fullWidth space={contentStackSpacing} justify="space-between">
                        <Text variant="h4">{intl('fastenConnections')}</Text>
                        <MedicalProfileUsageIndicator medicalProfileId={medicalProfileId} metricType="fasten-refresh" />
                    </Stack>

                    {/* Pending Connections Section */}
                    {hasPending && (
                        <Stack vertical fullWidth space={contentStackSpacing}>
                            <Text variant="h5" color="Gray-Text">
                                {intl('pendingConnections')}
                            </Text>

                            {/* Informational Alert */}
                            <Alert
                                type="info"
                                title="Processing Connection"
                                message={
                                    isPolling
                                        ? intl('pendingTimerMessage', { seconds: timeRemainingSeconds })
                                        : intl('pendingMessage')
                                }
                            />

                            {/* Pending connection cards */}
                            {pendingConnections.map((connection) => {
                                const logoUrl = connection.brand_id ? getFastenLogoUrl(connection.brand_id) : null;
                                const displayName =
                                    connection.brand_name || formatPlatformName(connection.platform_type);

                                return (
                                    <Card.Card
                                        key={connection.org_connection_id}
                                        testID="fasten-pending-connection-card"
                                        style={{ opacity: 0.7 }}
                                    >
                                        <Card.CardRightContent
                                            rightContent={
                                                <Stack vertical justify="center">
                                                    {logoUrl ? (
                                                        <View
                                                            style={{
                                                                width: 84,
                                                                height: 84,
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                backgroundColor: '#f9f9f9',
                                                                borderRadius: 8,
                                                            }}
                                                        >
                                                            <Image
                                                                source={{ uri: logoUrl }}
                                                                resizeMode="contain"
                                                                style={{
                                                                    width: 72,
                                                                    height: 72,
                                                                }}
                                                                accessibilityLabel={intl('logoAlt')}
                                                            />
                                                        </View>
                                                    ) : (
                                                        <View
                                                            style={{
                                                                width: 84,
                                                                height: 84,
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <ActivityIndicator size="small" color="#4F46E5" />
                                                        </View>
                                                    )}
                                                </Stack>
                                            }
                                        >
                                            <Card.VerticalSplitCardContent>
                                                <Card.CardContent>
                                                    <Card.CardHeader>{displayName}</Card.CardHeader>
                                                    <Card.CardChip title="Pending" />
                                                </Card.CardContent>
                                                <Card.CardContent>
                                                    <Card.CardBody2>{intl('pendingProcessing')}</Card.CardBody2>
                                                </Card.CardContent>
                                            </Card.VerticalSplitCardContent>
                                        </Card.CardRightContent>
                                    </Card.Card>
                                );
                            })}
                        </Stack>
                    )}

                    {/* Connected Section */}
                    {hasConnected && (
                        <Stack vertical fullWidth space={contentStackSpacing}>
                            {hasPending && (
                                <Text variant="h5" color="Gray-Text">
                                    {intl('connectedConnections')}
                                </Text>
                            )}

                            {filteredConnections.map((connection) => {
                                const dateString = getDateString(connection.date, 'date-time');
                                const lastRefreshDateString = connection.lastRefreshDateTime
                                    ? getDateString(connection.lastRefreshDateTime, 'date-time')
                                    : null;
                                const logoUrl = connection.brandId ? getFastenLogoUrl(connection.brandId) : null;
                                const isTefca = connection.connectionType === 'tefca';
                                const displayName =
                                    connection.institutionName ||
                                    (isTefca ? intl('tefcaNetwork') : formatPlatformName(connection.platformType));
                                const platformName = formatPlatformName(connection.platformType);
                                const isRefreshingConnection = connection.connectionStatus === 'refreshing';
                                const isInitialImport = isRefreshingConnection && !connection.lastRefreshDateTime;
                                const isRevoked = connection.connectionStatus === 'revoked';
                                const isRejected = connection.connectionStatus === 'rejected';
                                // Both states are dead ends until the patient re-authorizes, so both
                                // get the explanatory message. Only portal connections get the Reconnect
                                // CTA; a TEFCA connection is re-established through Fasten's widget.
                                const needsReconnect = isRevoked || isRejected;
                                const nextRefreshDate = getNextRefreshMonday(new Date());
                                const consentExpiringSoon =
                                    connection.consentExpiresAt && !needsReconnect
                                        ? isConsentExpiringSoon(connection.consentExpiresAt)
                                        : false;

                                return (
                                    <Card.Card
                                        key={connection.orgConnectionId}
                                        testID="fasten-connection-card"
                                        onPress={() => handleNavigateToConnection(connection.orgConnectionId)}
                                        style={{ paddingTop: 14, paddingBottom: 16, paddingLeft: 16, paddingRight: 16 }}
                                    >
                                        <Card.CardRightContent
                                            rightContent={
                                                <Stack vertical justify="center">
                                                    {logoUrl ? (
                                                        <View
                                                            style={{
                                                                width: 84,
                                                                height: 84,
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                backgroundColor: '#f9f9f9',
                                                                borderRadius: 8,
                                                            }}
                                                        >
                                                            <Image
                                                                source={{ uri: logoUrl }}
                                                                resizeMode="contain"
                                                                style={{
                                                                    width: 72,
                                                                    height: 72,
                                                                }}
                                                                accessibilityLabel={intl('logoAlt')}
                                                            />
                                                        </View>
                                                    ) : null}
                                                </Stack>
                                            }
                                        >
                                            <Card.VerticalSplitCardContent>
                                                <Card.CardContent>
                                                    <Card.CardHeader>{displayName}</Card.CardHeader>
                                                    {connection.patientName && (
                                                        <Card.CardBody color="Gray-Text">
                                                            {connection.patientName}
                                                        </Card.CardBody>
                                                    )}
                                                    <Stack horizontal space={2}>
                                                        {isRevoked ? (
                                                            <Card.CardChip title={intl('revoked')} />
                                                        ) : isRejected ? (
                                                            <Card.CardChip title={intl('rejected')} />
                                                        ) : isInitialImport ? (
                                                            <Card.CardChip
                                                                title={intl('importing')}
                                                                customColor="#5B9BD5"
                                                            />
                                                        ) : isRefreshingConnection ? (
                                                            <Card.CardChip
                                                                title={intl('refreshing')}
                                                                customColor="#5B9BD5"
                                                            />
                                                        ) : connection.connectionStatus === 'authorized' ? (
                                                            <Card.CardChip title="Connected" customColor="#5B9BD5" />
                                                        ) : (
                                                            <Card.CardChip
                                                                title={connection.connectionStatus}
                                                                customColor="#5B9BD5"
                                                            />
                                                        )}
                                                        {connection.platformType ? (
                                                            <Card.CardChip title={platformName} isSecondaryChip />
                                                        ) : null}
                                                        {connection.apiMode === 'test' ? (
                                                            <Card.CardChip title={intl('sandbox')} isSecondaryChip />
                                                        ) : null}
                                                        {isTefca ? (
                                                            <Card.CardChip title={intl('tefca')} isSecondaryChip />
                                                        ) : null}
                                                    </Stack>
                                                </Card.CardContent>
                                                <Card.CardContent>
                                                    <Card.CardBody2>
                                                        {intl('connected', { date: dateString })}
                                                    </Card.CardBody2>
                                                    {isInitialImport ? (
                                                        <Card.CardBody2 color="Gray-Text">
                                                            {intl('importingHealthRecords')}
                                                        </Card.CardBody2>
                                                    ) : lastRefreshDateString ? (
                                                        <Card.CardBody2 color="Gray-Text">
                                                            {intl('lastRefreshed', { date: lastRefreshDateString })}
                                                        </Card.CardBody2>
                                                    ) : null}
                                                    {needsReconnect ? (
                                                        <>
                                                            <Card.CardBody2 color="Alert">
                                                                {isRejected
                                                                    ? intl('rejectedMessage')
                                                                    : intl('revokedMessage')}
                                                            </Card.CardBody2>
                                                            {isTefca ? (
                                                                <Card.CardBody2 color="Gray-Text">
                                                                    {intl('tefcaReconnectMessage')}
                                                                </Card.CardBody2>
                                                            ) : hasPortalEditPermission ? (
                                                                <View style={{ marginTop: 8 }}>
                                                                    <Button
                                                                        title={
                                                                            hasReachedFastenRefreshLimit
                                                                                ? intl('refreshLimitReached')
                                                                                : intl('reconnect')
                                                                        }
                                                                        variant="secondary"
                                                                        size="sm"
                                                                        isDisabled={hasReachedFastenRefreshLimit}
                                                                        onPress={() => {
                                                                            navigation.navigate(
                                                                                'fasten-portal-connection',
                                                                                {
                                                                                    profileId: medicalProfileId,
                                                                                    orgConnectionId:
                                                                                        connection.orgConnectionId,
                                                                                    autoReconnect: 'true',
                                                                                },
                                                                            );
                                                                        }}
                                                                        testID="fasten-reconnect-button"
                                                                    />
                                                                </View>
                                                            ) : null}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Card.CardBody2 color="Success">
                                                                {intl('automaticallyRefreshingThrough', {
                                                                    date: getDateString(
                                                                        nextRefreshDate.toISOString(),
                                                                        'date-time',
                                                                    ),
                                                                })}
                                                            </Card.CardBody2>
                                                            {connection.consentExpiresAt && (
                                                                <Card.CardBody2
                                                                    color={consentExpiringSoon ? 'Alert' : 'Gray-Text'}
                                                                >
                                                                    {intl('consentValidUntil', {
                                                                        date: getDateString(
                                                                            connection.consentExpiresAt,
                                                                            'date-time',
                                                                        ),
                                                                    })}
                                                                </Card.CardBody2>
                                                            )}
                                                        </>
                                                    )}
                                                </Card.CardContent>
                                            </Card.VerticalSplitCardContent>
                                        </Card.CardRightContent>
                                    </Card.Card>
                                );
                            })}
                        </Stack>
                    )}

                    {/* Empty state - only show if both are empty */}
                    {!hasPending && !hasConnected && (
                        <Text variant="body-2" color="Gray-Text">
                            {intl('noConnections')}
                        </Text>
                    )}
                </Stack>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(FastenConnectionsList);
